'use client'

import Image from 'next/image'
import Link from 'next/link'

interface FooterProps {
  maxWidth?: string
}

const footerColumns = [
  {
    title: 'Studio',
    links: [
      { label: 'Products', href: '/#products' },
      { label: 'Labs', href: '/#labs' },
      { label: 'Open Source', href: '/#open-source' },
    ],
  },
  {
    title: 'Connect',
    links: [
      { label: 'GitHub', href: 'https://github.com/TechBuiltBySharan', external: true },
      { label: 'Writing', href: 'https://medium.builtbysharan.com', external: true },
      { label: 'Contact', href: '/contact' },
    ],
  },
]

export default function Footer({ maxWidth = 'max-w-4xl' }: FooterProps = {}) {
  return (
    <footer className={`${maxWidth} mx-auto px-6 sm:px-8 pb-8`}>
      {/* Link columns */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 pt-10 pb-8 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
        {footerColumns.map((column) => (
          <div key={column.title} className="flex flex-col gap-2.5">
            <span className="text-xs font-mono uppercase tracking-wider text-text-muted/60">{column.title}</span>
            {column.links.map((link) =>
              link.external ? (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-150 ease-out"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.label}
                  href={link.href}
                  className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-150 ease-out"
                >
                  {link.label}
                </Link>
              )
            )}
          </div>
        ))}
      </div>

      {/* Top Section */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 sm:gap-8 pt-8 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
        {/* Left: Brand + Subtext */}
        <div className="flex flex-col gap-3 min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-text-muted/60 font-light">Built with</span>
            <span className="text-red-500">❤️</span>
            <span className="text-sm text-text-muted/60 font-light">by</span>
            <Image 
              src="/logo.webp" 
              alt="BBS Logo" 
              width={24}
              height={24}
              className="object-contain ml-1 flex-shrink-0"
            />
          </div>
          <p className="text-xs text-text-muted/50 font-light leading-relaxed">
            Independent software, built by Sharan.
          </p>
        </div>

        {/* Right: Social Links */}
        <div className="flex items-center gap-3">
          <a 
            href="https://x.com/builtbysharan" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-text-muted/50 hover:text-text-muted transition-colors duration-150 ease-out font-mono focus:outline-none focus:ring-1 focus:ring-accent-primary/30 rounded-sm px-2 py-1.5"
            aria-label="Follow on X"
          >
            X
          </a>
          <span className="text-text-muted/30">·</span>
          <a 
            href="https://www.linkedin.com/company/builtbysharan" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-text-muted/50 hover:text-text-muted transition-colors duration-150 ease-out font-mono focus:outline-none focus:ring-1 focus:ring-accent-primary/30 rounded-sm px-2 py-1.5"
            aria-label="Connect on LinkedIn"
          >
            LinkedIn
          </a>
          <span className="text-text-muted/30">·</span>
          <a 
            href="https://medium.builtbysharan.com" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-xs text-text-muted/50 hover:text-text-muted transition-colors duration-150 ease-out font-mono focus:outline-none focus:ring-1 focus:ring-accent-primary/30 rounded-sm px-2 py-1.5"
            aria-label="Read on Medium"
          >
            Medium
          </a>
        </div>
      </div>

      {/* Bottom Section */}
      <div className="pt-6 mt-6 relative">
        <div className="absolute top-0 left-0 right-0 h-px bg-border-subtle opacity-60"></div>
        <div className="text-xs text-text-muted/50 font-mono">
          © {new Date().getFullYear()} BuiltBySharan
        </div>
      </div>
    </footer>
  )
}
